import React, { useMemo, useState } from 'react';
import StatusPill from '../../../../../components/StatusPill';
import { BALANCE_LEAVE_LABELS, BalanceLeaveType, LeaveEntry } from '../../../config';
import { countedYear, formatDay, formatLeaveDays, formatLeaveHours } from './leaveFormat';

interface LeaveTakenTableProps {
  leave: { accrual_start: string | null; current_year: number };
  entries: LeaveEntry[];
  leaveType: BalanceLeaveType;
  hoursPerDay: number;
  year: number;
}

const COLLAPSED_ROWS = 8;

/**
 * The leave entries on this person's timesheets, newest first. Read-only: a
 * leave entry is changed on the timesheet it belongs to, never from here.
 */
const LeaveTakenTable: React.FC<LeaveTakenTableProps> = ({ leave, entries, leaveType, hoursPerDay, year }) => {
  const [showAll, setShowAll] = useState<boolean>(false);

  const rows = useMemo(
    () => entries.filter((entry) =>
      entry.leave_type === leaveType && countedYear(entry.date, leaveType, leave) === year
    ),
    [entries, leaveType, year, leave]
  );
  const taken = useMemo(
    () => rows.reduce((sum, entry) => (entry.counted ? sum + Number(entry.hours) : sum), 0),
    [rows]
  );

  if (rows.length === 0) {
    return (
      <p className="ui-table__empty">
        No {BALANCE_LEAVE_LABELS[leaveType].toLowerCase()} on their timesheets in {year}.
      </p>
    );
  }

  const visible = showAll ? rows : rows.slice(0, COLLAPSED_ROWS);

  return (
    <>
      <table className="ui-table">
        <thead>
          <tr>
            <th className="col-date">Date</th>
            <th>Notes</th>
            <th>Week</th>
            <th className="is-num">Hours</th>
            <th className="is-num">Days</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((entry) => (
            <tr key={entry.id}>
              <td>{formatDay(entry.date)}</td>
              <td>{entry.description || <span className="ui-table__na">—</span>}</td>
              <td>
                {entry.counted
                  ? <StatusPill label="Counted" tone="success" />
                  : <StatusPill label="Not yet submitted" tone="warning" />}
              </td>
              <td className="is-num">{formatLeaveHours(Number(entry.hours))}</td>
              <td className="is-num ui-table__muted">
                {formatLeaveDays(Number(entry.hours), hoursPerDay)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="ui-table__totals">
          <tr>
            <td colSpan={3}>Taken</td>
            <td className="is-num">{formatLeaveHours(taken)}</td>
            <td className="is-num">{formatLeaveDays(taken, hoursPerDay)}</td>
          </tr>
        </tfoot>
      </table>
      {rows.length > COLLAPSED_ROWS && (
        <div className="chip-bar">
          <button type="button" className="link-btn" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show fewer' : `Show all ${rows.length}`}
          </button>
        </div>
      )}
    </>
  );
};

export default LeaveTakenTable;
